<template>
  <InfuseModal :title="t('e_connector.users.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-user" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.user.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="name"
              :maxlength="255"
              :placeholder="t('e_connector.users.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.user.password')" class="basis-full" size="small">
            <InfuseInput
              type="password"
              size="small"
              v-model="password"
              :minlength="8"
              :placeholder="t('e_connector.users.modal.field.password.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.users.modal.field.confirmPassword.label')"
            class="basis-full"
            size="small"
            :error="passwordsMatch ? '' : t('e_connector.users.modal.field.confirmPassword.error.noMatch')"
          >
            <InfuseInput
              type="password"
              size="small"
              v-model="confirmPassword"
              :minlength="8"
              :placeholder="t('e_connector.users.modal.field.confirmPassword.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="create-user" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CreateUser } from '@infuse/api/e-connector/user';

interface Props {
  loading: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', createUser: CreateUser): void;
}

withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const name = ref<string>('');
const password = ref<string>('');
const confirmPassword = ref<string>('');

const passwordsMatch = computed(() => !confirmPassword.value || password.value === confirmPassword.value);

function onSave() {
  if (!passwordsMatch.value) {
    return;
  }

  emit('create', {
    name: name.value,
    password: password.value
  });
}
</script>
